import isBrowser from './is-browser';

/**
 * Generates breadcrumbs based on the location pathname.
 *
 * @param {object} location - The location object containing the pathname.
 * @param {boolean} showLastSlash - Optional parameter to determine if the last slash should be shown.
 * @return {JSX.Element | null} The generated breadcrumbs as a JSX element or null if the location is not available.
 */
export const generateBreadcrumbs = (
  location: { pathname: any } | null,
  showLastSlash?: boolean,
  workspaceName?: string,
) => {
  if (!isBrowser() || !location) return null;

  const { pathname } = location;
  const paths = pathname
    .split('/')
    .filter((path: string | any[]) => path.length > 0);

  return (
    <div className="flex items-center text-sm ml-1">
      {paths.map((path: string, index: number) => {
        const isLast = showLastSlash ? false : index === paths.length - 1;

        return (
          <div key={path} className="flex items-center">
            <span
              className={`${
                isLast && !showLastSlash ? 'opacity-100' : 'opacity-40'
              } capitalize`}
            >
              {path}
            </span>
            {!isLast && <span className="opacity-40 mx-2">/</span>}
          </div>
        );
      })}
      {workspaceName && <span className="ml-0">{workspaceName}</span>}
    </div>
  );
};

export const NAVIGATION_HEIGHT = 65; // Change this to the desired height in Tailwind units
export const NAVIGATION_HEIGHT_OFFSET = NAVIGATION_HEIGHT + 2; // Adjust as needed
export const SIDEBAR_WIDTH = 287; // Change this to the desired width in Tailwind units
export const EASE_ARRAY = [0, 0.78, 0, 0.99];
export const EASE_CSS = `cubic-bezier(${EASE_ARRAY.join(',')})`;

export const iconClassName = 'min-h-[24px] max-h-[24px] min-w-[24px]';

export const createNewChatBtnClassName =
  'flex flex-col w-[100%] w-full min-h-[14rem] p-4 !h-auto text-center !text-black justify-start max-w-[378px] self-stretch shadow-none hover:!bg-secondary-400 dark:hover:!bg-primary-400 transition-colors ease-in-out bg-secondary-500 dark:bg-primary-500 transition-colors ease-in-out';

export const fillerWords =
  "a about above after against although among an and are aren't around as at be because been before being below between but by did do does doing down during except for from had has have having how if in into is isn't like near nor now of off on once only or out over since so that the then these this those though through to toward under unless until up upon was wasn't were weren't what when whenever where whereas wherever whether which while who whom whose why with without yet can could may might must shall should will would do don't doesn't didn't our we're we've we'll we'd you're you've you'll you'd he's he'll he'd she's she'll she'd it's it'll it'd I'm I've I'll I'd isn't aren't wasn't weren't haven't hasn't hadn't won't wouldn't shouldn't couldn't can't don't didn't doesn't do did in on at by off your you're you've you'll you'd we're we've we'll we'd they're they've they'll they'd she's she'll she'd he's he'll he'd it's it'll it'd isn't aren't wasn't weren't haven't hasn't hadn't won't wouldn't shouldn't couldn't can't don't didn't doesn't do did in on at by off you";

export const additional_filter_words =
  'allow permits permit gives give lets let enables enable requires require involves involve leads lead helps help makes make keeps keep starts start begins begin goes go takes take sees see comes come puts put sets set gets get says say does do goes go';

export const transparentGradientFX =
  ' bg-gradient-to-r from-green-300 via-blue-500 to-purple-600 bg-gradient-to-br bg-clip-text text-transparent animate-hue';

export const linkClassName =
  'inline-flex items-center justify-center rounded-md font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:[&>*]:translate-y-0.5 text-primary underline-offset-4 hover:underline text-sm [&>*]:px-4 [&>*>span]:!pt-0 !px-1 !py-0 h-[24px]';

export const trimQueryString = (query: string, maxLength = 1500) => {
  const trimNotice = '... (clipped rest of message)';
  // Define the maximum length, considering enough space for the notice
  const maxEffectiveLength = maxLength - trimNotice.length;

  // Check if the query exceeds the effective maximum length
  if (query.length > maxEffectiveLength) {
    // Trim the query and append the clipping notice
    return query.substring(0, maxEffectiveLength) + trimNotice;
  }
  // Return the original query if it doesn't exceed the maximum length
  return query;
};
